import { createFileRoute } from '@tanstack/react-router';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Search, Bot, User, ChevronDown, ChevronRight } from 'lucide-react';
import { api } from '@/api/client';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { FilterChip } from '@/components/ui/filter-chip';
import { AdminPageHeader } from '@/components/admin/AdminPageHeader';
import { cn } from '@/lib/utils';
import { humanizeAuditAction } from '@/lib/audit';
import type {
  AdminModuleAuditEntry,
  AdminModuleAuditListResponse,
} from '@/api/generated/types.gen';

export const Route = createFileRoute('/admin/audit')({
  component: AdminAudit,
});

type SourceFilter = 'all' | 'User' | 'Agent';

const PAGE_SIZE = 50;

function AdminAudit() {
  const [source, setSource] = useState<SourceFilter>('all');
  const [actionFilter, setActionFilter] = useState<string>('all');
  const [returnId, setReturnId] = useState('');
  const [q, setQ] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const queryString = (cursor?: number) => {
    const p = new URLSearchParams({ take: String(PAGE_SIZE) });
    if (source !== 'all') p.set('source', source);
    if (actionFilter !== 'all') p.set('action', actionFilter);
    if (returnId.trim()) p.set('returnId', returnId.trim());
    if (q.trim()) p.set('q', q.trim());
    if (from) p.set('from', new Date(from).toISOString());
    if (to) p.set('to', new Date(to).toISOString());
    if (cursor) p.set('beforeId', String(cursor));
    return p.toString();
  };

  const keyParts = useMemo(
    () => ({ source, actionFilter, returnId: returnId.trim(), q: q.trim(), from, to }),
    [source, actionFilter, returnId, q, from, to],
  );

  const entries = useInfiniteQuery({
    queryKey: ['admin', 'audit', keyParts],
    queryFn: ({ pageParam }) =>
      api<AdminModuleAuditListResponse>(
        `/admin/audit?${queryString(pageParam as number | undefined)}`,
      ),
    initialPageParam: undefined as number | undefined,
    getNextPageParam: (lastPage) =>
      lastPage.nextCursor != null ? Number(lastPage.nextCursor) : undefined,
    retry: false,
  });

  const actions = useQuery({
    queryKey: ['admin', 'audit', 'actions'],
    queryFn: () => api<string[]>('/admin/audit/actions'),
    staleTime: 5 * 60_000,
    retry: false,
  });

  const allRows: AdminModuleAuditEntry[] =
    entries.data?.pages.flatMap((p) => p.items ?? []) ?? [];

  return (
    <div className="space-y-6">
      <AdminPageHeader
        eyebrow="Reviewer console"
        title="Audit log"
        description={
          <>
            Every mutation that flows through the MediatR pipeline. Sourced from the{' '}
            <code className="rounded-(--radius-sm) bg-(--color-paper-2) px-1.5 py-0.5 font-mono text-[12px]">
              AuditLogBehaviour
            </code>
            .
          </>
        }
      />

      <div className="space-y-3 rounded-(--radius) border border-(--color-rule) bg-(--color-card) p-4">
        <div className="flex flex-wrap items-center gap-3">
          <FilterChip label="All sources" active={source === 'all'} onClick={() => setSource('all')} />
          <FilterChip label="User" active={source === 'User'} onClick={() => setSource('User')} />
          <FilterChip label="Agent" active={source === 'Agent'} onClick={() => setSource('Agent')} />
          <span className="ml-2 h-6 w-px bg-(--color-rule)" aria-hidden />
          <Select
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
            className="w-auto"
            aria-label="Filter by action"
          >
            <option value="all">All actions</option>
            {(actions.data ?? []).map((a) => (
              <option key={a} value={a}>
                {humanizeAuditAction(a)}
              </option>
            ))}
          </Select>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative min-w-60 flex-1">
            <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-(--color-ink-muted)" />
            <Input
              placeholder="Search action, entity, or user…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-10"
            />
          </div>
          <Input
            placeholder="Return ID"
            value={returnId}
            onChange={(e) => setReturnId(e.target.value)}
            className="w-32"
            inputMode="numeric"
          />
          <DateField label="From" value={from} onChange={setFrom} />
          <DateField label="To" value={to} onChange={setTo} />
        </div>
      </div>

      <div className="overflow-hidden rounded-(--radius) border border-(--color-rule) bg-(--color-card)">
        {entries.isLoading ? (
          <Empty>Loading audit log…</Empty>
        ) : entries.isError ? (
          <Empty>Audit endpoint unavailable.</Empty>
        ) : allRows.length === 0 ? (
          <Empty>No entries.</Empty>
        ) : (
          <ul className="divide-y divide-(--color-rule)">
            {allRows.map((e) => (
              <AuditRow key={String(e.id)} entry={e} />
            ))}
          </ul>
        )}
      </div>

      <div className="flex items-center justify-between">
        <p className="text-[12px] text-(--color-ink-muted)">
          Showing {allRows.length} entr{allRows.length === 1 ? 'y' : 'ies'}.
        </p>
        {entries.hasNextPage && (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => entries.fetchNextPage()}
            disabled={entries.isFetchingNextPage}
          >
            {entries.isFetchingNextPage ? 'Loading…' : 'Load older'}
          </Button>
        )}
      </div>
    </div>
  );
}

function AuditRow({ entry: a }: { entry: AdminModuleAuditEntry }) {
  const [open, setOpen] = useState(false);
  const hasPayload = !!a.payload;
  const isAgent = a.source === 'Agent';
  return (
    <li>
      <button
        type="button"
        onClick={() => hasPayload && setOpen((v) => !v)}
        className={cn(
          'flex w-full items-center justify-between gap-3 px-4 py-3 text-left text-[13px]',
          hasPayload ? 'cursor-pointer hover:bg-(--color-paper-2)' : 'cursor-default',
        )}
      >
        <div className="flex min-w-0 items-center gap-3">
          {hasPayload ? (
            open ? (
              <ChevronDown className="size-4 shrink-0 text-(--color-ink-muted)" />
            ) : (
              <ChevronRight className="size-4 shrink-0 text-(--color-ink-muted)" />
            )
          ) : (
            <span className="block size-4 shrink-0" aria-hidden />
          )}
          <div
            className={cn(
              'flex size-8 shrink-0 items-center justify-center rounded-(--radius-sm)',
              isAgent
                ? 'bg-(--color-navy-soft) text-(--color-navy)'
                : 'bg-(--color-paper-3) text-(--color-ink-2)',
            )}
          >
            {isAgent ? (
              <Bot className="size-4" strokeWidth={1.7} />
            ) : (
              <User className="size-4" strokeWidth={1.7} />
            )}
          </div>
          <div className="min-w-0">
            <div className="truncate font-medium text-(--color-ink)">
              {humanizeAuditAction(a.action)}
            </div>
            <div className="truncate text-[12px] text-(--color-ink-muted)">
              <span className="font-mono text-[11px] text-(--color-ink-subtle)">{a.action ?? ''}</span>
              {a.userName ? ` · ${a.userName}` : ''}
              {a.returnId != null ? ` · return #${a.returnId}` : ''}
            </div>
          </div>
        </div>
        <div className="font-mono text-[11px] tabular-nums text-(--color-ink-muted)">
          {new Date(a.timestamp).toLocaleString('en-AU')}
        </div>
      </button>
      {open && hasPayload && (
        <div className="border-t border-(--color-rule) bg-(--color-paper-2) px-4 py-3">
          <div className="section-ref mb-2">Payload (PII-scrubbed)</div>
          <pre className="overflow-x-auto font-mono text-[11px] leading-relaxed text-(--color-ink-2)">
            {prettyJson(a.payload!)}
          </pre>
        </div>
      )}
    </li>
  );
}

function DateField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <label className="flex items-center gap-2 text-[12px] text-(--color-ink-muted)">
      {label}
      <Input type="date" value={value} onChange={(e) => onChange(e.target.value)} className="w-auto" />
    </label>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return (
    <p className="px-5 py-10 text-center text-[13px] text-(--color-ink-muted)">{children}</p>
  );
}

function prettyJson(raw: string): string {
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
}
